/**
 * Cascade Flip Animation
 *
 * Triangles randomly flip with a 3D Z-axis spin effect.
 * Neighbors have a chance to flip when one completes.
 * Supports coverage presets for partial grid layouts.
 *
 * Uses tri-cube coordinate system for topologically correct neighbors.
 */

import type { TriCubeCoord } from './core/types';
import { neighbors } from './core/tri-cube';

export type CoveragePreset = 'full' | 'top' | 'bottom' | 'left' | 'right' | 'center' | 'vignette';

export interface FlipState {
  isFlipping: boolean;
  progress: number;       // 0 to 1
  startTime: number;
  flipDuration: number;   // ms
  flipped: boolean;       // Is currently in flipped state (after 180°)
  triggered: boolean;     // Has this flip triggered neighbors?
}

export interface CascadeConfig {
  flipDuration: number;       // Duration of one flip in ms
  flipChance: number;         // Chance per second for random flip (0-1)
  cascadeChance: number;      // Chance to trigger neighbor (0-1)
  cascadeDelay: number;       // Delay before neighbor flips (ms)
  coverage: CoveragePreset;   // Which part of grid to cover
  coverageFalloff: number;    // How gradual the edge falloff is (0-1)
}

/** Minimal triangle info needed by cascade (avoids circular dep) */
export interface TriangleInfo {
  coord: TriCubeCoord;
  normalizedX: number;
  normalizedY: number;
}

const DEFAULT_CASCADE_CONFIG: CascadeConfig = {
  flipDuration: 800,          // Slower, more graceful flip
  flipChance: 0.003,          // Much rarer - ~0.3% per second per triangle
  cascadeChance: 0.15,        // Lower cascade chance
  cascadeDelay: 150,          // Slightly longer delay for subtlety
  coverage: 'vignette',       // Default to vignette (edges)
  coverageFalloff: 0.15,      // Sharper edge falloff
};

/**
 * Calculate coverage density at a normalized position
 * Returns 0-1 where 1 = full density, 0 = no triangles
 */
function getCoverageDensity(
  nx: number,  // 0-1 normalized x
  ny: number,  // 0-1 normalized y
  coverage: CoveragePreset,
  falloff: number
): number {
  // Attempt to handle values as decimals (0.1-0.9) - if outside range, normalize
  const x = Math.max(0, Math.min(1, nx));
  const y = Math.max(0, Math.min(1, ny));

  // Smoothstep with sharper transition
  const smoothstep = (edge0: number, edge1: number, val: number): number => {
    const t = Math.max(0, Math.min(1, (val - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  };

  // Coverage depth - how far the coverage extends (0.15 = 15% of screen)
  const depth = 0.15 + falloff * 0.2;

  switch (coverage) {
    case 'full':
      return 1;

    case 'top':
      // Dense at top (y=0), fades as y increases
      if (y > depth + falloff) return 0;
      return smoothstep(depth + falloff, 0, y);

    case 'bottom':
      // Dense at bottom (y=1), fades as y decreases
      if (y < 1 - depth - falloff) return 0;
      return smoothstep(1 - depth - falloff, 1, y);

    case 'left':
      // Dense at left (x=0), fades as x increases
      if (x > depth + falloff) return 0;
      return smoothstep(depth + falloff, 0, x);

    case 'right':
      // Dense at right (x=1), fades as x decreases
      if (x < 1 - depth - falloff) return 0;
      return smoothstep(1 - depth - falloff, 1, x);

    case 'center': {
      // Dense in center, fades to edges
      const centerDist = Math.sqrt((x - 0.5) ** 2 + (y - 0.5) ** 2);
      const centerRadius = 0.25 + falloff * 0.15;
      if (centerDist > centerRadius + falloff * 0.5) return 0;
      return smoothstep(centerRadius + falloff * 0.5, 0, centerDist);
    }

    case 'vignette': {
      // Dense at edges, empty in center
      const edgeDist = Math.min(x, 1 - x, y, 1 - y);
      const vignetteDepth = depth + falloff * 0.1;
      if (edgeDist > vignetteDepth) return 0;
      return smoothstep(vignetteDepth, 0, edgeDist);
    }

    default:
      return 1;
  }
}

export class CascadeSystem {
  private states: Map<number, FlipState> = new Map();
  private pendingCascades: Array<{ index: number; triggerTime: number }> = [];
  private config: CascadeConfig;
  private neighborMap: Map<number, number[]> = new Map();
  private trianglePositions: Map<number, { nx: number; ny: number }> = new Map();

  constructor(config: Partial<CascadeConfig> = {}) {
    this.config = { ...DEFAULT_CASCADE_CONFIG, ...config };
  }

  /**
   * Initialize the system with triangle data and coordinate lookup map.
   */
  init(
    triangleCount: number,
    coordIndexMap: Map<string, number>,
    triangles: TriangleInfo[],
  ): void {
    this.states.clear();
    this.pendingCascades = [];
    this.trianglePositions.clear();

    // Initialize all triangles with default state and store positions
    for (let i = 0; i < triangleCount; i++) {
      this.states.set(i, {
        isFlipping: false,
        progress: 0,
        startTime: 0,
        flipDuration: this.config.flipDuration,
        flipped: false,
        triggered: false,
      });

      const tri = triangles[i];
      this.trianglePositions.set(i, { nx: tri.normalizedX, ny: tri.normalizedY });
    }

    // Build neighbor map using tri-cube topology
    this.buildNeighborMap(coordIndexMap, triangles);
  }

  /**
   * Build map of neighbors for each triangle using tri-cube neighbors()
   */
  private buildNeighborMap(
    coordIndexMap: Map<string, number>,
    triangles: TriangleInfo[],
  ): void {
    this.neighborMap.clear();

    for (let i = 0; i < triangles.length; i++) {
      const tri = triangles[i];
      const triNeighbors = neighbors(tri.coord); // exactly 3 edge-sharing
      const indices: number[] = [];

      for (const nb of triNeighbors) {
        const key = `${nb.a},${nb.b},${nb.c}`;
        const idx = coordIndexMap.get(key);
        if (idx !== undefined) {
          indices.push(idx);
        }
        // neighbors outside viewport silently skipped
      }

      this.neighborMap.set(i, indices);
    }
  }

  /**
   * Update all triangle states
   */
  update(currentTime: number, deltaTime: number): void {
    // Process pending cascades
    this.processPendingCascades(currentTime);

    // Random flip triggers
    this.processRandomFlips(currentTime, deltaTime);

    // Update flipping triangles
    this.updateFlippingTriangles(currentTime);
  }

  /**
   * Process pending cascade triggers
   */
  private processPendingCascades(currentTime: number): void {
    const toRemove: number[] = [];

    for (let i = 0; i < this.pendingCascades.length; i++) {
      const cascade = this.pendingCascades[i];
      if (currentTime >= cascade.triggerTime) {
        this.triggerFlip(cascade.index, currentTime);
        toRemove.push(i);
      }
    }

    // Remove processed cascades (in reverse order to maintain indices)
    for (let i = toRemove.length - 1; i >= 0; i--) {
      this.pendingCascades.splice(toRemove[i], 1);
    }
  }

  /**
   * Random chance to trigger flips
   */
  private processRandomFlips(currentTime: number, deltaTime: number): void {
    const chancePerFrame = this.config.flipChance * (deltaTime / 1000);

    for (const [index, state] of this.states) {
      if (state.isFlipping) continue;

      // Check coverage - only flip triangles in covered areas
      const pos = this.trianglePositions.get(index);
      if (pos) {
        const density = getCoverageDensity(
          pos.nx,
          pos.ny,
          this.config.coverage,
          this.config.coverageFalloff
        );
        // Skip if outside coverage area or randomly based on density
        if (density < 0.1 || Math.random() > density) continue;
      }

      if (Math.random() < chancePerFrame) {
        this.triggerFlip(index, currentTime);
      }
    }
  }

  /**
   * Update triangles that are currently flipping
   */
  private updateFlippingTriangles(currentTime: number): void {
    for (const [index, state] of this.states) {
      if (!state.isFlipping) continue;

      const elapsed = currentTime - state.startTime;
      state.progress = Math.min(elapsed / state.flipDuration, 1);

      // Trigger cascade when halfway through
      if (state.progress >= 0.5 && !state.triggered) {
        state.triggered = true;
        this.triggerCascade(index, currentTime);
      }

      // Complete flip
      if (state.progress >= 1) {
        state.isFlipping = false;
        state.progress = 0;
        state.flipped = !state.flipped;
      }
    }
  }

  /**
   * Trigger a flip for a specific triangle
   */
  triggerFlip(index: number, currentTime: number): void {
    const state = this.states.get(index);
    if (!state || state.isFlipping) return;

    state.isFlipping = true;
    state.progress = 0;
    state.startTime = currentTime;
    state.triggered = false;
    // Vary flip duration slightly for organic feel
    state.flipDuration = this.config.flipDuration * (0.85 + Math.random() * 0.3);
  }

  /**
   * Potentially trigger neighbor flips
   */
  private triggerCascade(index: number, currentTime: number): void {
    const nbrs = this.neighborMap.get(index);
    if (!nbrs) return;

    for (const neighborIndex of nbrs) {
      const neighborState = this.states.get(neighborIndex);
      if (!neighborState || neighborState.isFlipping) continue;

      // Check if already pending
      const alreadyPending = this.pendingCascades.some(c => c.index === neighborIndex);
      if (alreadyPending) continue;

      // Check coverage for neighbor
      const pos = this.trianglePositions.get(neighborIndex);
      if (pos) {
        const density = getCoverageDensity(
          pos.nx,
          pos.ny,
          this.config.coverage,
          this.config.coverageFalloff
        );
        if (density < 0.1) continue;
      }

      // Random chance to cascade
      if (Math.random() < this.config.cascadeChance) {
        const delay = this.config.cascadeDelay * (0.7 + Math.random() * 0.6);
        this.pendingCascades.push({
          index: neighborIndex,
          triggerTime: currentTime + delay,
        });
      }
    }
  }

  /**
   * Get the current state for a triangle
   * Returns scaleX for 3D flip effect (simulates Z-rotation)
   */
  getState(index: number): { scaleX: number; opacity: number } {
    const state = this.states.get(index);
    const pos = this.trianglePositions.get(index);

    // Calculate base opacity from coverage
    let baseOpacity = 1;
    if (pos) {
      baseOpacity = getCoverageDensity(
        pos.nx,
        pos.ny,
        this.config.coverage,
        this.config.coverageFalloff
      );
    }

    if (!state) {
      return { scaleX: 1, opacity: baseOpacity };
    }

    if (!state.isFlipping) {
      return { scaleX: 1, opacity: baseOpacity };
    }

    // 3D flip effect using cosine for scaleX
    // Progress 0->0.5: triangle rotates from 0 to 90 degrees (face disappears)
    // Progress 0.5->1: triangle rotates from 90 to 180 degrees (back appears)
    // Use smooth easing for gentle motion
    const eased = this.easeInOutSine(state.progress);
    const angle = eased * Math.PI; // 0 to PI (180 degrees)
    const scaleX = Math.abs(Math.cos(angle));

    // Slight opacity boost when flipping
    const opacity = baseOpacity * (0.9 + 0.1 * (1 - Math.abs(scaleX - 0.5) * 2));

    return { scaleX: Math.max(0.02, scaleX), opacity };
  }

  /**
   * Update config
   */
  setConfig(config: Partial<CascadeConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get config
   */
  getConfig(): CascadeConfig {
    return { ...this.config };
  }

  // Smooth easing - no bounce
  private easeInOutSine(t: number): number {
    return -(Math.cos(Math.PI * t) - 1) / 2;
  }
}
